import { ErrorMessage } from '@hookform/error-message';
import classnames from 'classnames';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import React, { CSSProperties, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Select, {
  CSSObjectWithLabel,
  GroupBase,
  MenuListProps,
  MultiValueGenericProps,
  OptionProps,
  Props,
  SelectInstance,
  StylesConfig,
} from 'react-select';
import { components } from 'react-select';

import styles from './SelectField.module.css';

import CreateOptionButton from '@/shared/components/atoms/SelectField/CreateOptionButton';
import { ISelectFieldProps } from '@/shared/components/atoms/SelectField/ISelectField';
import { Typography } from '@/shared/components/atoms/Typography';

export default function SelectField<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: Props<Option, IsMulti, Group> &
    ISelectFieldProps<Option, IsMulti, Group>
) {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation('common');
  const customControlStyles: CSSProperties = {
    // color: textColor,
    borderColor: errors[props?.errorMessageId ?? props?.id]
      ? '#CC4949'
      : props?.borderColor,
    fontSize: props?.fontSize,
    paddingLeft: props?.px,
    paddingRight: props?.px,
    paddingTop: props?.py,
    paddingBottom: props?.py,
    borderRadius: props?.borderRadius || '0.5rem',
    backgroundColor: props?.backgroundColor,
    outlineColor: 'transparent',
  };
  const selectStyles:
    | StylesConfig<Option, (IsMulti & false) | (IsMulti & true), Group>
    | undefined = {
    option: (provided, state) => {
      const temp: CSSObjectWithLabel = {
        ...provided,
        zIndex: 99,
      };
      if (state.isSelected) {
        temp.backgroundColor = '#C8DDF5';
      }
      return temp;
    },

    control: (provided) => {
      return {
        ...provided,

        ...customControlStyles,
      };
    },

    menuPortal: (provided) => {
      return {
        ...provided,
        zIndex: 50,
      };
    },

    multiValue: (provided) => {
      return {
        ...provided,
        borderRadius: '8px',
      };
    },

    multiValueRemove: (provided) => {
      return {
        ...provided,
        borderRadius: '8px',
      };
    },
  };

  const labelClasses = classnames(styles['label'], {
    [styles['label-disabled']]: props?.isDisabled,
  });

  const options: Option[] =
    props?.options?.flatMap((group: GroupBase<Option>) => group.options) ?? [];

  const Option = (optionProps: OptionProps<Option, IsMulti, Group>) => (
    <components.Option {...optionProps}>
      {props?.customOption?.(optionProps)}
    </components.Option>
  );

  // used to blur or focus or the field, useful for closing the menu after a certain action
  const selectRef = useRef<SelectInstance<Option, IsMulti, Group> | null>(null);

  const MenuList = ({
    children,
    ...menuListProps
  }: MenuListProps<Option, IsMulti, Group>) => (
    <components.MenuList {...menuListProps}>
      {props?.customMenuList ? (
        <>
          {props?.createOption && (
            <CreateOptionButton
              title={props?.createOption?.title ?? ''}
              subTitle={props?.createOption?.subTitle ?? ''}
              onClick={() => {
                props?.createOption?.onClick();
                // close menu after opening modal
                selectRef.current?.blur();
              }}
            />
          )}
          {props.customMenuList({ children, ...menuListProps }, selectRef)}
        </>
      ) : props?.createOption ? (
        <>
          <CreateOptionButton
            title={props.createOption.title}
            subTitle={props.createOption.subTitle}
            onClick={() => {
              props.createOption?.onClick();
              // close menu after opening modal
              selectRef.current?.blur();
            }}
          />
          {children}
        </>
      ) : (
        children
      )}
    </components.MenuList>
  );

  const MultiValueContainer = (
    multiValueProps: MultiValueGenericProps<Option, IsMulti, Group>
  ) => {
    return props?.customMultiValueContainer ? (
      props?.customMultiValueContainer(
        <components.MultiValueContainer {...multiValueProps} />,
        multiValueProps.data
      )
    ) : (
      <components.MultiValueContainer {...multiValueProps} />
    );
  };

  return (
    <div className={`relative  flex ${props?.width} min-w-[27%] flex-col`}>
      {props?.label ? (
        <label htmlFor={props?.id} className={labelClasses}>
          <Typography variant='body2' weight='regular'>
            {props?.label}{' '}
            {props?.required && <span className={styles['required']}>*</span>}
          </Typography>
        </label>
      ) : null}
      <div className='relative'>
        <Controller
          name={props?.id}
          control={control}
          defaultValue={props?.defaultValue}
          render={({ field: { onChange, value, ...rest } }) => (
            <Select
              {...rest}
              {...props}
              ref={selectRef}
              className={clsx(
                'rounded-xl',
                props?.isDisabled && styles['disabled']
              )}
              // serialize value to be like option
              value={
                props?.value ||
                (props.isGroup
                  ? options.filter((item: Option) =>
                      props.isMulti
                        ? value?.includes(props.getValue(item))
                        : value === props.getValue(item)
                    )
                  : props?.list?.filter((item: Option) =>
                      props.isMulti
                        ? value?.includes(props.getValue(item))
                        : value === props.getValue(item)
                    ))
              }
              // if there are custom component for rendering options
              components={{
                ...(props.customOption && { Option }),
                MenuList,
                MultiValueContainer,
              }}
              options={props.options ?? props?.list}
              getOptionLabel={props?.getLabel}
              getOptionValue={props?.getValue}
              // only send value to onchange
              onChange={
                props?.onChange ??
                ((e) => {
                  if (props?.isMulti) {
                    // assert 'e' is an array
                    onChange(
                      (e as Option[]).map((item: Option) =>
                        props?.getValue(item)
                      )
                    );
                  } else {
                    // assert 'e' is a single value
                    onChange(props?.getValue(e as Option));
                  }
                })
              }
              styles={selectStyles}
              noOptionsMessage={
                props?.noOptionsMessage
                  ? props?.noOptionsMessage
                  : () => t('no-options-message')
              }
              menuPortalTarget={document.body}
            />
          )}
        />
      </div>
      <ErrorMessage
        errors={errors}
        name={props?.errorMessageId ?? props?.id}
        render={({ message }: { message: string }) => (
          <Typography
            className='mt-8 text-error-default'
            variant='body2'
            weight='regular'
          >
            {message}
          </Typography>
        )}
      />
      {props?.isDisabled && props?.disabledMessage && (
        <Typography variant='subtitle2' className='mt-4 opacity-4'>
          {props.disabledMessage}
        </Typography>
      )}
    </div>
  );
}

SelectField.defaultProps = {
  py: '2px',
  px: '',
  textColor: 'text-text-default',
  fontSize: 'text-base',
  borderColor: 'border-background-disable',
  placeholderColor: 'text-text-secondary',
};
